/**
 * The portal route map (portal IA overhaul, 2026-09).
 *
 * One capability, one page body, up to two mounts:
 *
 * - the ORG mount (`/wallet`, `/keys`, …) is what members and enterprise
 *   admins use — the viewer's own organization, resolved server-side from
 *   the session; role-gated capabilities such as Billing stay admin-only;
 * - the CONSOLE mount (`/admin/clients/<id>/wallet`, …) is the super-admin
 *   client console: the same page body pinned to one client by the URL,
 *   because platform accounts have no organization of their own.
 *
 * Every link, redirect and sidebar entry derives its href from this table so
 * the two vocabularies can never drift apart. Enterprise admins who land on
 * a console URL of their own org are canonicalized to the org mount
 * (`consolePathToOrgPath`, applied in proxy.ts); legacy paths live in
 * `legacy-routes.ts`.
 *
 * Dependency-free on purpose: proxy.ts (edge runtime), next.config.ts and
 * "use server" modules all import it.
 */

export const CONSOLE_PREFIX = "/admin/clients";

/** Stable ids for every client-scoped capability with a page. */
export type ClientCapability =
  | "dashboard"
  | "eventLog"
  | "wallet"
  | "awsBilling"
  | "walletCategories"
  | "walletTransfer"
  | "walletInvite"
  | "analytics"
  | "analyticsBusiness"
  | "customerPricing"
  | "keys"
  | "org"
  | "orgGuide"
  | "activity"
  | "notifications"
  | "privacy"
  | "integrations"
  | "models"
  | "pricing"
  | "routing"
  | "gpu"
  | "resources"
  | "serverless"
  | "endpoints"
  | "runpod"
  | "aws"
  | "containers"
  | "secrets"
  | "voice"
  | "training"
  | "context";

interface CapabilityRoute {
  /** Org mount, absolute path. */
  org: string;
  /** Console mount segment under `/admin/clients/<id>/` ("" = the client root). */
  console: string;
}

export const CLIENT_ROUTES: Record<ClientCapability, CapabilityRoute> = {
  dashboard: { org: "/home", console: "" },
  eventLog: { org: "/observe/events", console: "observe/events" },
  wallet: { org: "/wallet", console: "wallet" },
  awsBilling: { org: "/wallet/aws", console: "wallet/aws" },
  walletCategories: { org: "/wallet/categories", console: "wallet/categories" },
  // Transfer › Transfer (funding codes) and Transfer › Invite (client
  // invites): own pages, in their own sidebar section next to Billing.
  walletTransfer: { org: "/wallet/transfer", console: "wallet/transfer" },
  walletInvite: { org: "/wallet/invite", console: "wallet/invite" },
  analytics: { org: "/analytics", console: "analytics" },
  analyticsBusiness: { org: "/analytics/business", console: "analytics/business" },
  // Prices an org charges its own end customers (AIR-1479): the third
  // Analytics view, next to the Spend by Customer card it feeds.
  customerPricing: { org: "/analytics/customers/pricing", console: "analytics/customers/pricing" },
  keys: { org: "/keys", console: "keys" },
  org: { org: "/org", console: "org" },
  orgGuide: { org: "/org/guide", console: "org/guide" },
  activity: { org: "/activity", console: "activity" },
  notifications: { org: "/notifications", console: "notifications" },
  privacy: { org: "/privacy-controls", console: "privacy-controls" },
  integrations: { org: "/help/integrations", console: "integrations" },
  models: { org: "/models", console: "models" },
  pricing: { org: "/models/pricing", console: "models/pricing" },
  routing: { org: "/models/routing", console: "models/routing" },
  gpu: { org: "/gpu", console: "gpu" },
  // My Resources: everything the org is running (machines, dedicated
  // endpoints, capacity requests) on one management page. The GPU page is
  // for deploying; it only shows an overview that links here.
  resources: { org: "/resources", console: "resources" },
  // The serverless model browser is a GPU sub-surface on both mounts
  // (`/gpu/serverless/<owner>/<name>` is a model's detail view).
  serverless: { org: "/gpu/serverless", console: "gpu/serverless" },
  // Dedicated deployments (model → hardware → review → live) on both mounts.
  // `/gpu/endpoints` remains the stable URL even though the product label is broader.
  endpoints: { org: "/gpu/endpoints", console: "gpu/endpoints" },
  // RunPod organization tenancy (AIR-1554): the org's assigned RunPod
  // organization, its RunPod credentials and the client-connection snippets.
  // The upstream assignment itself is a super-admin card on API Keys.
  runpod: { org: "/gpu/runpod", console: "gpu/runpod" },
  // The AWS Hub (AIR-1616): the org's own AWS account on one page — the
  // account and its credentials, the Quickstart playbooks, the federated
  // console — with the two AWS workspaces as sub-paths: Elastic Container
  // Service (`containers`, the clusters/services/tasks/logs workspace,
  // reached from My Resources and the Quickstart rather than the sidebar)
  // and Secrets Manager (`secrets`, a tab of the hub with its own path so
  // `?secret=` deep links survive).
  aws: { org: "/aws", console: "aws" },
  containers: { org: "/aws/ecs", console: "aws/ecs" },
  secrets: { org: "/aws/secrets", console: "aws/secrets" },
  voice: { org: "/voice", console: "voice" },
  training: { org: "/lab/training", console: "lab/training" },
  context: { org: "/context", console: "context" },
};

/** Routes that are per-user or platform-wide: identical for every mount. */
export const GLOBAL_ROUTES = {
  quickstart: "/quickstart",
  /** Developers › Playground: try models in the browser, nothing saved (per viewer, so no console twin). */
  playground: "/playground",
  quickstartCodingAgents: "/quickstart/coding-agents",
  tools: "/tools",
  documents: "/documents",
  help: "/help",
  profile: "/profile",
  platformAdmin: "/admin",
  clients: "/admin/clients",
} as const;

/** Guided setup pages that replaced the legacy `/connect/*` page family. */
export const DEVELOPER_GUIDE_ROUTES = {
  claudeDesktop: "/quickstart/coding-agents/claude-desktop",
  claudeCode: "/quickstart/coding-agents/claude-code",
  cline: "/quickstart/coding-agents/cline",
  codex: "/quickstart/coding-agents/codex",
  copilot: "/quickstart/coding-agents/copilot",
  cursor: "/quickstart/coding-agents/cursor",
  droid: "/quickstart/coding-agents/droid",
  githubActions: "/quickstart/coding-agents/github-actions",
  mcp: "/quickstart/coding-agents/mcp",
  microsoftOffice: "/quickstart/coding-agents/microsoft-office",
  zed: "/quickstart/coding-agents/zed",
} as const;

export const DEVELOPER_DOCS_URL = "https://docs.aireserve.com";

/**
 * The developer-docs pages the portal hands off to instead of mirroring
 * (glass redesign): Quickstart's two tiles, and the "every other tool" link
 * under Coding Agents. The portal keeps no copy of these guides.
 */
export const DEVELOPER_DOCS_PAGES = {
  cli: `${DEVELOPER_DOCS_URL}/connect/cli`,
  sdk: `${DEVELOPER_DOCS_URL}/connect/openai-sdk`,
  connect: `${DEVELOPER_DOCS_URL}/connect`,
} as const;

/** Href of a capability on the org mount. */
export function orgPath(capability: ClientCapability): string {
  return CLIENT_ROUTES[capability].org;
}

/** Which mount a shared client page body is rendering on. */
export type ClientPageMount = "org" | "console";

/** Href of a sibling capability from inside a page body, on the same mount the body renders on. */
export function clientHref(mount: ClientPageMount, clientId: string, capability: ClientCapability): string {
  return mount === "org" ? orgPath(capability) : consolePath(clientId, capability);
}

/**
 * Rebases a console href (as data loaders still build them, e.g. attention
 * items) onto the org mount when a shared body renders there; query and hash
 * survive. Console mounts and non-console hrefs pass through untouched.
 */
export function hrefForMount(mount: ClientPageMount, href: string): string {
  if (mount === "console") return href;
  const { path, rest } = splitHref(href);
  return `${consolePathToOrgPath(path) ?? path}${rest}`;
}

/** `{ path, rest }` — the pathname and whatever query/hash follows it. */
function splitHref(href: string): { path: string; rest: string } {
  const match = href.match(/^([^?#]*)(.*)$/);
  return { path: match?.[1] ?? href, rest: match?.[2] ?? "" };
}

/**
 * Route-table entries longest first, so `/models/routing` matches before
 * `/models` and `models/routing` before `models`. Computed once: the
 * serverless grid calls `siblingHref` per card.
 */
const CAPABILITIES_LONGEST_ORG_FIRST: readonly ClientCapability[] = (
  Object.keys(CLIENT_ROUTES) as ClientCapability[]
).sort((a, b) => CLIENT_ROUTES[b].org.length - CLIENT_ROUTES[a].org.length);
const CAPABILITIES_LONGEST_CONSOLE_FIRST: readonly ClientCapability[] = (
  Object.keys(CLIENT_ROUTES) as ClientCapability[]
).sort((a, b) => CLIENT_ROUTES[b].console.length - CLIENT_ROUTES[a].console.length);

/**
 * Which mount the page at `pathname` renders on, as a client component can
 * tell from `usePathname()`: inside `/admin/clients/<id>/…` it is the
 * console pinned to that client, anywhere else the viewer's own org. Shared
 * bodies use this to point sibling links (`clientHref`) at the same mount
 * they are on, so a super admin reading a client's pages never gets sent to
 * an org page — which has no organization for them and bounces to the
 * clients list.
 */
export function mountForPath(
  pathname: string,
): { mount: "console"; clientId: string } | { mount: "org"; clientId: null } {
  const parsed = parseConsolePath(pathname);
  return parsed ? { mount: "console", clientId: parsed.clientId } : { mount: "org", clientId: null };
}

/**
 * The same capability from `pathname`'s mount: the console twin of an org
 * path when `pathname` is inside a client console, the org path otherwise.
 * `orgHref` must be an org-mount href from the route table (query and hash
 * survive); anything else passes through untouched.
 */
export function siblingHref(pathname: string, orgHref: string): string {
  const scope = mountForPath(pathname);
  if (scope.mount === "org") return orgHref;
  const { path, rest } = splitHref(orgHref);
  const twin = orgPathToConsolePath(scope.clientId, path);
  return twin ? `${twin}${rest}` : orgHref;
}

/**
 * The console twin of an org-mount path for `clientId`, or null when the path
 * is not in the route table. Longest org path first so `/models/routing`
 * wins over `/models`; a deeper suffix (`/gpu/serverless/meta/llama`) rides
 * along.
 */
export function orgPathToConsolePath(clientId: string, pathname: string): string | null {
  const path = pathname.replace(/\/$/, "") || "/";
  for (const capability of CAPABILITIES_LONGEST_ORG_FIRST) {
    const org = CLIENT_ROUTES[capability].org;
    if (path === org) return consolePath(clientId, capability);
    if (path.startsWith(`${org}/`)) return `${consolePath(clientId, capability)}${path.slice(org.length)}`;
  }
  return null;
}

/** Href of a capability on the super-admin console mount for `clientId`. */
export function consolePath(clientId: string, capability: ClientCapability): string {
  const segment = CLIENT_ROUTES[capability].console;
  const base = `${CONSOLE_PREFIX}/${clientId}`;
  return segment ? `${base}/${segment}` : base;
}

/** `{ clientId, rest }` when `pathname` is inside a client console, else null. */
export function parseConsolePath(pathname: string): { clientId: string; rest: string } | null {
  const match = pathname.match(/^\/admin\/clients\/([^/]+)(\/.*)?$/);
  if (!match) return null;
  return { clientId: match[1]!, rest: match[2] ?? "" };
}

/**
 * The org-mount equivalent of a console path, or null when the path is not
 * a console path or has no org twin. Console segments and org paths are
 * looked up in the route table (longest segment first, so `models/routing`
 * wins over `models`), and any deeper suffix or query is preserved.
 */
export function consolePathToOrgPath(pathname: string): string | null {
  const parsed = parseConsolePath(pathname);
  if (!parsed) return null;
  const rest = parsed.rest.replace(/\/$/, "");
  for (const capability of CAPABILITIES_LONGEST_CONSOLE_FIRST) {
    const segment = CLIENT_ROUTES[capability].console;
    const consoleRest = segment ? `/${segment}` : "";
    if (rest === consoleRest) return CLIENT_ROUTES[capability].org;
    if (segment && rest.startsWith(`${consoleRest}/`)) {
      return `${CLIENT_ROUTES[capability].org}${rest.slice(consoleRest.length)}`;
    }
  }
  return null;
}
